/**
 * Client-side Excel-export van het maandrooster (offline, ExcelJS).
 *
 * Eén tab "Maandrooster" met alle weken als blokken onder elkaar, plus per week
 * een tab "Weekrooster wk N" met de kleurenmatrix én een daglijst eronder.
 * De cel-codes/kleuren en de voorrangslogica komen uit lib/roostercodes.js,
 * zodat scherm en export uit één bron putten.
 *
 * Bordeaux-huisstijl: HOOFD #5A1A32, SUB #8B2D52, dienst-codes in CEL-kleuren.
 */

import { getCel, isWerkCode, daglijstGroep, daglijstVolgorde, legendaCodesVoor } from './roostercodes.js';
import { schoolweekType } from '../engine/rooster-engine.js';
import { bouwAfwezigheidTypeMap } from '../data/afwezigheid.js';
import { vakantieDagenInWeek } from '../data/vakanties.js';
import { logError } from './logger.js';
import { isRoosterbaar } from './rollen.js';

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const DAG_LABEL = {
  ma: 'Maandag', di: 'Dinsdag', wo: 'Woensdag', do: 'Donderdag',
  vr: 'Vrijdag', za: 'Zaterdag', zo: 'Zondag',
};

// De daglijst-groepen, hun volgorde, de werk-regel voor de totaalrij en de legenda
// komen alle vier uit roostercodes.js — hier stonden ze als vier losse lijsten die
// dezelfde letters herhaalden en bij een nieuwe code uit de pas zouden lopen.

// ── Kleur/stijl-helpers ──────────────────────────────────────────────────────
const argb = (hex) => 'FF' + hex.replace('#', '');
const HOOFD_FILL = 'FF5A1A32';
const SUB_FILL = 'FF8B2D52';
const WIT = 'FFFFFFFF';

const fillSolid = (kleur) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: kleur } });
const DUN = { style: 'thin', color: { argb: 'FFD9D9D9' } };
const ALLE_RANDEN = { top: DUN, left: DUN, bottom: DUN, right: DUN };

function styleHoofd(cell) {
  cell.fill = fillSolid(HOOFD_FILL);
  cell.font = { color: { argb: WIT }, bold: true };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
  cell.border = ALLE_RANDEN;
}
function styleSub(cell) {
  cell.fill = fillSolid(SUB_FILL);
  cell.font = { color: { argb: WIT }, bold: true };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
  cell.border = ALLE_RANDEN;
}
function styleData(cell) {
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
  cell.border = ALLE_RANDEN;
}
function styleCelObj(cell, celObj) {
  cell.value = celObj.code;
  cell.fill = fillSolid(argb(celObj.bg));
  cell.font = { color: { argb: argb(celObj.fg) }, bold: true };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
  cell.border = ALLE_RANDEN;
}

// ── Datum-helpers (w.maandag is een 'YYYY-MM-DD'-string) ──────────────────────
function parseMaandag(maandag) {
  if (maandag instanceof Date) {
    return new Date(maandag.getFullYear(), maandag.getMonth(), maandag.getDate());
  }
  const [y, m, d] = String(maandag).split('-').map(Number);
  return new Date(y, m - 1, d);
}
function datumVoorDag(maandag, dagIndex) {
  const d = parseMaandag(maandag);
  d.setDate(d.getDate() + dagIndex);
  return d;
}
function ddmm(date) {
  const p = (n) => String(n).padStart(2, '0');
  return `${p(date.getDate())}-${p(date.getMonth() + 1)}`;
}

// ── Matrix-blok (kop + 7 dagrijen), gedeeld door beide tabbladen ──────────────
function schrijfMatrix(sheet, mwKeys, namen, codes, maandag, { rotateHeader = false } = {}) {
  const kopRow = sheet.addRow(['Datum', ...namen]);
  kopRow.eachCell((cell, col) => {
    styleHoofd(cell);
    if (rotateHeader && col > 1) {
      cell.alignment = { vertical: 'bottom', horizontal: 'center', textRotation: 90 };
    }
  });
  DAGEN.forEach((dag, di) => {
    const datum = datumVoorDag(maandag, di);
    const rowVals = [`${DAG_LABEL[dag]} ${ddmm(datum)}`];
    for (const k of mwKeys) rowVals.push(codes[dag][k]?.code ?? '');
    const row = sheet.addRow(rowVals);
    const eerste = row.getCell(1);
    styleData(eerste);
    eerste.alignment = { vertical: 'middle', horizontal: 'left' };
    mwKeys.forEach((k, ki) => {
      const cell = row.getCell(ki + 2);
      const celObj = codes[dag][k];
      if (celObj) styleCelObj(cell, celObj);
      else styleData(cell);
    });
  });
}

// ── Legenda-blok (codes + labels), onderaan een tab ──────────────────────────
// `codes` = de cellen die in DEZE omgeving voorkomen (legendaCodesVoor): elke dienst
// via celCodeVoorDienst, daarna S/K/V/Z/A. De gereserveerde RI/G/OW blijven hier
// bewust buiten, net als hiervoor — die staan alleen op het scherm.
function schrijfLegenda(sheet, startCol = 2, codes = []) {
  const cCode = startCol;        // kolom voor "Legenda"-kop EN de code-swatch
  const cLabel = startCol + 1;   // kolom voor het label
  const labelRow = sheet.addRow([]);
  labelRow.getCell(cCode).value = 'Legenda';
  styleHoofd(labelRow.getCell(cCode));
  labelRow.getCell(cCode).alignment = { vertical: 'middle', horizontal: 'left' };
  for (const celObj of codes) {
    const row = sheet.addRow([]);
    row.getCell(cCode).value = celObj.code;
    styleCelObj(row.getCell(cCode), celObj); // code in eigen CEL-kleur
    const labelCell = row.getCell(cLabel); // het label
    labelCell.value = celObj.label;
    labelCell.alignment = { vertical: 'middle', horizontal: 'left' };
    labelCell.border = ALLE_RANDEN;
  }
}

// ── Hoofdfunctie ──────────────────────────────────────────────────────────────
export async function exporteerMaandroosterExcel(maandrooster, regels, afwezigheid, maandLabel, celOverrides, bijzonderheden, vakanties = []) {
  try {
    const { default: ExcelJS } = await import('exceljs');

    // Kolomvolgorde op naam — zelfde ordening als de matrix in de app, zodat de export
    // en het scherm naast elkaar te leggen zijn.
    const mwKeys = Object.keys(regels.medewerkers)
      .filter((k) => !k.startsWith('_') && isRoosterbaar(regels.medewerkers[k]))
      .sort((a, b) => regels.medewerkers[a].naam.localeCompare(regels.medewerkers[b].naam, 'nl'));
    const namen = mwKeys.map((k) => regels.medewerkers[k].naam);
    const weken = maandrooster?.weken ?? [];
    const afw = afwezigheid ?? [];

    // De legenda van deze omgeving: één keer afleiden, beide tabbladen gebruiken hem.
    const legendaCodes = legendaCodesVoor(regels);

    // Per week vooraf de code-matrix berekenen (hergebruikt door beide tabbladen).
    // codes[dag][k] = CEL-object of null.
    const perWeek = weken.map((w) => {
      const weekType = schoolweekType(w.weeknummer);
      const afwTypeWeek = bouwAfwezigheidTypeMap(afw, w.weeknummer, w.isoJaar);
      // Vakantiedagen van deze week (Set<dag-code>), zelfde bouw als in de app: in een
      // schoolvakantie vervalt de schooldag, dus geen S-cel in de export.
      const vakantieDagen = new Set(vakantieDagenInWeek(w.weeknummer, w.isoJaar, vakanties));
      const codes = {};
      for (const dag of DAGEN) {
        codes[dag] = {};
        for (const k of mwKeys) {
          const mw = regels.medewerkers[k];
          codes[dag][k] = getCel(mw.naam, dag, w.rooster, mw, weekType, w.weeknummer, afwTypeWeek?.[mw.naam], celOverrides?.[w.weeknummer]?.[mw.naam]?.[dag], regels, vakantieDagen);
        }
      }
      return { w, codes };
    });

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'SASS Rooster';
    workbook.created = new Date();

    // ── Tab "Maandrooster": alle weken als blokken onder elkaar ──────────────
    const maandSheet = workbook.addWorksheet('Maandrooster');
    maandSheet.getColumn(1).width = 16;
    for (let i = 0; i < mwKeys.length; i++) maandSheet.getColumn(i + 2).width = 6;

    for (const { w, codes } of perWeek) {
      const ma = datumVoorDag(w.maandag, 0);
      const zo = datumVoorDag(w.maandag, 6);

      // 1. weeklabel-rij (HOOFD), over de volle breedte
      const labelRow = maandSheet.addRow([`Week ${w.weeknummer} — ${ddmm(ma)} t/m ${ddmm(zo)}`]);
      for (let c = 1; c <= mwKeys.length + 1; c++) styleHoofd(labelRow.getCell(c));
      maandSheet.mergeCells(labelRow.number, 1, labelRow.number, mwKeys.length + 1);

      // 2. kop-rij + 3. dagrijen (namen geroteerd → compacte maandweergave)
      schrijfMatrix(maandSheet, mwKeys, namen, codes, w.maandag, { rotateHeader: true });

      // 4. totaalrij (SUB): aantal werk-codes per medewerker
      const totalen = mwKeys.map((k) =>
        DAGEN.reduce((n, dag) => n + (isWerkCode(codes[dag][k]?.code) ? 1 : 0), 0),
      );
      const totaalRow = maandSheet.addRow(['Totaal', ...totalen]);
      totaalRow.eachCell((cell) => styleSub(cell));

      // 5. lege scheidingsrij
      maandSheet.addRow([]);
    }

    // Legenda onderaan (laatste week-blok eindigt al met een lege scheidingsrij).
    // Kolom A (=16 breed) zodat de "Legenda"-kop niet afkapt; kolom B is hier maar 6.
    schrijfLegenda(maandSheet, 1, legendaCodes);

    // ── Per week: tab "Weekrooster wk N" (matrix + daglijst) ─────────────────
    for (const { w, codes } of perWeek) {
      const sheet = workbook.addWorksheet(`Weekrooster wk ${w.weeknummer}`);
      sheet.getColumn(1).width = 16;
      for (let i = 0; i < mwKeys.length + 3; i++) sheet.getColumn(i + 2).width = 13;

      // A. Matrix (namen horizontaal, breder zodat de daglijst eronder leesbaar is)
      schrijfMatrix(sheet, mwKeys, namen, codes, w.maandag, { rotateHeader: false });
      sheet.addRow([]);

      // B. Daglijst per dag
      DAGEN.forEach((dag, di) => {
        const datum = datumVoorDag(w.maandag, di);
        const subRow = sheet.addRow([`${DAG_LABEL[dag]} ${ddmm(datum)}`]);
        styleSub(subRow.getCell(1));

        // groepeer aanwezigen per daglijst-groep (afwezig V/Z/A overslaan).
        // daglijstGroep valt voor een code zonder vaste groep terug op het label,
        // dus niemand verdwijnt hier nog stil uit de lijst. De cel zelf bewaren we
        // mee: die levert de kleur van het groeplabel (was de GROEP_CODE-omweg).
        const perGroep = {};
        for (const k of mwKeys) {
          const celObj = codes[dag][k];
          if (!celObj) continue;
          const groep = daglijstGroep(celObj);
          if (!groep || groep === 'afwezig') continue;
          (perGroep[groep] ??= { celObj, namen: [] }).namen.push(regels.medewerkers[k].naam);
        }

        for (const groep of daglijstVolgorde(Object.keys(perGroep))) {
          const lijst = perGroep[groep]?.namen;
          if (!lijst || lijst.length === 0) continue;
          // kolom A leeg, B = groeplabel, C.. = namen (1 per kolom)
          const row = sheet.addRow(['', groep, ...lijst]);

          const labelCell = row.getCell(2);
          const celObj = perGroep[groep].celObj;
          if (celObj) {
            labelCell.fill = fillSolid(argb(celObj.bg));
            labelCell.font = { color: { argb: argb(celObj.fg) }, bold: true };
          } else {
            labelCell.font = { bold: true };
          }
          labelCell.alignment = { vertical: 'middle', horizontal: 'left' };
          labelCell.border = ALLE_RANDEN;

          for (let i = 0; i < lijst.length; i++) {
            const naamCell = row.getCell(3 + i);
            naamCell.alignment = { vertical: 'middle', horizontal: 'left' };
            naamCell.border = ALLE_RANDEN;
          }
        }

        // Afspraken-blok onder de groep-rijen: alleen tonen als de teamleider voor deze
        // dag iets invulde (bijzonderheden[weeknummer][dag]). De echte notitie komt in
        // één rij, cel B t/m F gemerged met wrapText zodat lange tekst leesbaar blijft.
        // Geen tekst → geen blok (vervangt bewust de oude handmatige invul-box).
        const afspraak = bijzonderheden?.[w.weeknummer]?.[dag];
        if (afspraak && afspraak.trim()) {
          const afsprLabelRow = sheet.addRow(['', 'Afspraken']);
          styleSub(afsprLabelRow.getCell(2));
          afsprLabelRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };

          const tekstRow = sheet.addRow(['', afspraak.trim()]);
          tekstRow.height = 28; // ruimte voor ~2 regels; een merged cel groeit niet auto mee
          for (let c = 2; c <= 6; c++) tekstRow.getCell(c).border = ALLE_RANDEN;
          tekstRow.getCell(2).alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
          sheet.mergeCells(tekstRow.number, 2, tekstRow.number, 6);
        }

        sheet.addRow([]); // luchtregel tussen de dagen
      });

      // Legenda onderaan de weektab (laatste dag eindigt al met een lege rij)
      schrijfLegenda(sheet, 2, legendaCodes);
    }

    // ── Download via tijdelijke <a> ──────────────────────────────────────────
    const buf = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Maandrooster_${String(maandLabel ?? 'maand').replace(/\s+/g, '_')}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    logError(err, { source: 'export', feature: 'excel-maandrooster', maandLabel });
    return false;
  }
}
